import { z } from "zod";

export const AI_PROVIDERS = ["openai", "anthropic", "google", "mistral"] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];
export const DEFAULT_AI_PROVIDER: AiProvider = "openai";
export const DEFAULT_AI_MODEL: Record<AiProvider, string> = {
  openai: "gpt-5-mini",
  anthropic: "claude-sonnet-4-5",
  google: "gemini-2.5-flash",
  mistral: "mistral-medium-latest",
};

export const aiSettingsSchema = z.object({
  provider: z.enum(AI_PROVIDERS),
  model: z.string().trim().max(100).nullable().optional(),
  /** chiave propria del tenant: scritta, mai restituita. Stringa vuota = rimuovi. */
  apiKey: z.string().trim().max(400).optional(),
  enabled: z.boolean().default(true),
  /** Kipo AI anche per i clienti dei progetti (omesso = invariato) */
  clientsEnabled: z.boolean().optional(),
  /** richieste al mese per ogni cliente */
  clientMonthlyLimit: z.number().int().min(0).max(1000).optional(),
  /** chat agentica con registro del lavoro e ragionamento visibili (omesso = invariato) */
  deepChat: z.boolean().optional(),
  /** lettura del codice dei repository collegati (omesso = invariato) */
  codeAccess: z.boolean().optional(),
});

export const aiChatSchema = z.object({
  conversationId: z.string().uuid().optional(),
  message: z.string().trim().min(1).max(8000),
  /** contesto della pagina corrente (modulo ed entità aperta) */
  context: z
    .object({ module: z.string().max(40).optional(), entityType: z.string().max(40).optional(), entityId: z.string().uuid().optional() })
    .optional(),
  /** conferma esplicita di un'azione proposta (tool che modifica dati) */
  confirmToolCallId: z.string().max(100).optional(),
});
export type AiChatInput = z.infer<typeof aiChatSchema>;

// ── risposte visive di Kipo AI (prodotte dai tool show_*, disegnate dalla web app) ──
export const aiChartBlockSchema = z.object({
  type: z.literal("chart"),
  title: z.string().trim().min(1).max(120),
  /** column: confronto o andamento per periodo · bar: classifica · line: andamento continuo */
  kind: z.enum(["column", "bar", "line"]),
  /** unità mostrata accanto ai valori, es. "€", "h", "task" */
  unit: z.string().trim().max(12).nullable(),
  data: z.array(z.object({ label: z.string().trim().min(1).max(60), value: z.number().finite() })).min(1).max(30),
});
export const aiStatsBlockSchema = z.object({
  type: z.literal("stats"),
  items: z.array(z.object({ label: z.string().trim().min(1).max(60), value: z.string().trim().min(1).max(40), hint: z.string().trim().max(80).nullable(), tone: z.enum(["default", "good", "warn"]) })).min(1).max(6),
});
export const aiTableBlockSchema = z.object({
  type: z.literal("table"),
  title: z.string().trim().min(1).max(120),
  columns: z.array(z.string().trim().min(1).max(40)).min(1).max(6),
  rows: z.array(z.array(z.string().max(200))).min(1).max(30),
});
/** Percorso interno di Kipo (es. /projects/<id>?task=<id>): mai URL esterni o javascript:. */
export const aiAppPathSchema = z.string().trim().max(300).regex(/^\/[A-Za-z0-9\-_/]*(\?[A-Za-z0-9\-_=&%.]*)?$/, "Percorso interno non valido");
export const aiItemsBlockSchema = z.object({
  type: z.literal("items"),
  title: z.string().trim().min(1).max(120),
  items: z
    .array(
      z.object({
        title: z.string().trim().min(1).max(160),
        /** es. nome del progetto o del cliente */
        subtitle: z.string().trim().max(120).nullable(),
        /** dettagli brevi: assegnatario, scadenza, ore, importo */
        meta: z.array(z.string().trim().min(1).max(60)).max(4),
        status: z.object({ label: z.string().trim().min(1).max(30), tone: z.enum(["default", "active", "good", "warn"]) }).nullable(),
        /** link restituito dai tool, es. /projects/<id> o /projects/<id>?task=<id> */
        href: aiAppPathSchema,
      }),
    )
    .min(1)
    .max(30),
});
export const aiBlockSchema = z.discriminatedUnion("type", [aiChartBlockSchema, aiStatsBlockSchema, aiTableBlockSchema, aiItemsBlockSchema]);
export type AiBlock = z.infer<typeof aiBlockSchema>;

/** Un passaggio dell'agente nel registro del lavoro (chat agentica). */
export interface AiAgentStep {
  id: string;
  kind: "tool" | "thought";
  /** nome del tool o "Ragionamento" */
  label: string;
  status: "running" | "done" | "failed";
  input?: string;
  output?: string;
  startedAt: number;
  endedAt?: number;
}
